import {BrowserModule} from '@angular/platform-browser';
import {NgModule, provideZoneChangeDetection} from '@angular/core';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {HTTP_INTERCEPTORS, HttpClientJsonpModule, HttpClientModule} from '@angular/common/http';
import {AuthHttpInterceptor} from './helpers/auth-http.interceptor';
import {ErrorHttpInterceptor} from './helpers/error-http.interceptor';
import {NgxsModule} from '@ngxs/store';
import {environment} from '../environments/environment';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {NgxsStoragePluginModule} from '@ngxs/storage-plugin';
import {AuthState} from './state/auth.state';
import {LoginComponent} from './pages/login/login.component';
import {MatCardModule} from '@angular/material/card';
import {CommonModule} from '@angular/common';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatListModule} from '@angular/material/list';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {PosState} from './state/pos.state';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {ProductsState} from './state/products.state';
import {QuillModule} from 'ngx-quill';
import {UsersState} from './state/users.state';
import {ErrorComponent} from './pages/error/error.component';
import {AccountsState} from './state/accounts.state';
import {ShippingState} from './state/shipping.state';
import {PaymentsState} from './state/payments.state';
import {JobStatusState} from './state/jobStatus.state';
import {XeroState} from './state/xero.state';
import {OrganizationState} from './state/organization.state';
import {CommonAppModule} from './common.module';
import {TermsComponent} from './pages/terms/terms.component';
import {PrivacyPolicyComponent} from './pages/privacy-policy/privacy-policy.component';
import {SearchState} from './state/search.state';
import {RefrigeratorState} from './state/refrigerator.state';
import {ProcessFlowState} from './state/processFlow.state';
import {HomeComponent} from './pages/home/home.component';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {DocumentState} from './state/documents.state';
import {SettingsState} from './state/settings.state';
import {LottieComponent, provideLottieOptions, provideCacheableAnimationLoader} from 'ngx-lottie';
import player from 'lottie-web';
import {SettingsComponent} from './pages/settings/settings.component';
import {DeliveryNoteState} from './state/deliveryNote.state';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {LineItemsState} from './state/lineItems.state';
import {EngineState} from './state/engine.state';
import {InstrumentState} from './state/instrument.state';
import {CreditNoteState} from './state/creditNote.state';
import {LabourState} from './state/labour.state';
import {StockState} from './state/stock.state';

import {ServiceWorkerModule} from '@angular/service-worker';
import {MAT_DATE_LOCALE} from '@angular/material/core';
import {SubscriptionState} from './state/subscription.state';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS} from '@angular/material-moment-adapter';
import {PayInvoiceComponent} from './pages/pay-invoice/pay-invoice.component';
import {NgxStripeModule} from 'ngx-stripe';
import {LoggingState} from './state/logging.state';
import {AnalyticsState} from './state/analytics.state';
import {provideCharts, withDefaultRegisterables} from 'ng2-charts';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ErrorComponent,
    TermsComponent,
    PrivacyPolicyComponent,
    HomeComponent,
    SettingsComponent,
    PayInvoiceComponent,
  ],
  imports: [
    // Angular Default
    CommonModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    HttpClientJsonpModule,
    FormsModule,
    ReactiveFormsModule,
    CommonAppModule,
    // NGXS
    NgxsModule.forRoot([
      AuthState,
      PosState,
      ProductsState,
      UsersState,
      AccountsState,
      ShippingState,
      PaymentsState,
      OrganizationState,
      SearchState,
      RefrigeratorState,
      ProcessFlowState,
      DocumentState,
      SettingsState,
      DeliveryNoteState,
      LineItemsState,
      EngineState,
      InstrumentState,
      CreditNoteState,
      LabourState,
      StockState,
      SubscriptionState,
      AnalyticsState,
      LoggingState,
      XeroState,
      JobStatusState
    ], {
      developmentMode: !environment.production
    }),
    NgxsStoragePluginModule.forRoot({
      keys: [
        'auth.token',
        'auth.organization',
        'auth.expires',
        'auth.username',
        'auth.organizationModules',
        'auth.userId',
        'auth.roles',
        'organization.organization',
        'organization.currencySymbol',
        'settings'
      ]
    }),
    NgxStripeModule.forRoot(environment.STRIPE_PUBLISHABLE_KEY),
    // Angular Material
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatSidenavModule,
    MatToolbarModule,
    MatListModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatSlideToggleModule,
    // ngx-quill
    QuillModule.forRoot(),
    LottieComponent,
    MatCheckboxModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    })
  ],
  providers: [
    provideZoneChangeDetection({eventCoalescing: true}),
    {provide: HTTP_INTERCEPTORS, useClass: ErrorHttpInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true},
    {provide: MAT_DATE_LOCALE, useValue: 'en-GB'},
    {provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: {useUtc: true}},
    provideLottieOptions({player: () => player}),
    provideCacheableAnimationLoader(),
    provideCharts(withDefaultRegisterables()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}
